import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { MdOutlineEdit, MdOutlineRemoveRedEye, MdInfoOutline } from 'react-icons/md';
import DataTable from 'components/Common/DataTable';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useRestBusiness } from 'apis/agency/hook';
import { BusinessProfileResponse } from 'apis/business/types';
import { Tag } from '@/apis/agency/taggings/types';
import { useDestroyTagging } from '@/apis/agency/taggings/hook';
import ResetBusinessButton from './ResetBusinessButton';
import Pagination from 'components/Common/Pagination';
import { CacheKeys } from 'utils/cacheKeys';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useOpenBusinessWorkspace } from '../hooks/useOpenBusinessWorkspace';
import { PackageResponse } from '@/apis/agency/businessPackages/types';
import dayjs from 'dayjs';
import { getStatusStyle } from './businessStatusUtils';
import BusinessTagPill from './BusinessTagPill';
import InactiveStatusTooltip from './InactiveStatusTooltip';

interface BusinessesListingProps {
  businesses?: BusinessProfileResponse[];
  isLoading: boolean;
  totalPages?: number;
  currentPage: number;
  perPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onAddTags: (business: BusinessProfileResponse) => void;
  onViewAddons: (business: BusinessProfileResponse) => void;
}

export default function BusinessesListing({
  businesses,
  isLoading,
  totalPages,
  currentPage,
  perPage,
  totalCount,
  onPageChange,
  onPerPageChange,
  onAddTags,
  onViewAddons
}: BusinessesListingProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { setSelectedBusiness, setSelectedLocation } = useBusiness();
  const { openBusinessWorkspace } = useOpenBusinessWorkspace();
  const { mutate: resetBusiness } = useRestBusiness();
  const { mutate: destroyTag } = useDestroyTagging();

  const handleEditBusiness = (business: BusinessProfileResponse) => {
    if (business) {
      setSelectedBusiness(Number(business.id));
      setSelectedLocation(Number(business?.locations?.[0]?.id));
      localStorage.setItem(CacheKeys.businessIds, JSON.stringify([business.id]));
      localStorage.setItem(CacheKeys.locationIds, JSON.stringify(business?.locations?.map((item) => item.id)));
      navigate(`businesses/${business.id}`);
    }
  };

  const handleToggleBusiness = (business: BusinessProfileResponse) => {
    openBusinessWorkspace(business);
  };

  const handleRemoveTag = (tag: Tag) => {
    destroyTag(tag.id);
  };

  return (
    <>
      <DataTable
        data={businesses}
        columns={columns(
          theme,
          handleEditBusiness,
          handleToggleBusiness,
          resetBusiness,
          onAddTags,
          handleRemoveTag,
          onViewAddons
        )}
        isHeaderSticky={true}
        isLoading={isLoading}
      />
      <Pagination
        totalPages={totalPages}
        totalCount={totalCount}
        currentPage={currentPage}
        perPage={perPage}
        onChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </>
  );
}

type HandleToggleBusinessFn = (business: BusinessProfileResponse) => void;

const columns = (
  theme: any,
  handleEditBusiness: (business: BusinessProfileResponse) => void,
  handleToggleBusiness: HandleToggleBusinessFn,
  onReset: (id: number) => void,
  onAddTags: (business: BusinessProfileResponse) => void,
  onRemoveTag: (tag: Tag) => void,
  onViewAddons: (business: BusinessProfileResponse) => void
): ColumnDef<BusinessProfileResponse>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: (info) => (
      <Typography
        sx={{
          wordBreak: 'break-word',
          maxWidth: { xs: '120px', sm: '200px', md: 'none' },
          fontSize: { xs: '14px', sm: '16px' }
        }}
      >
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Business Code',
    accessorKey: 'uid'
  },
  {
    header: 'Email',
    accessorKey: 'email',
    cell: (info) => (
      <Typography
        sx={{
          wordBreak: 'break-word',
          maxWidth: { xs: '150px', sm: '250px', md: 'none' },
          fontSize: { xs: '14px', sm: '16px' }
        }}
      >
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Locations',
    accessorKey: 'locations_count'
  },
  {
    header: 'Billing Start',
    accessorKey: 'plan_started_at',
    cell: (info) => {
      const date = info.getValue() as string;
      if (!date) return '-';
      return (
        <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{dayjs(date).format('MMM DD, YYYY')}</Typography>
      );
    }
  },
  {
    header: 'PACKAGE',
    accessorKey: 'package',
    cell: (info) => getPackageDetails(info.row.original.business_plan)
  },
  {
    header: 'Account Status',
    cell: (info) => {
      const business = info.row.original;
      const accountStatus = business.account_status;
      const isBillingCancellation = business.cancellation_reason;
      const style = getStatusStyle(theme, accountStatus);

      if (accountStatus === 'inactive' && isBillingCancellation) {
        return (
          <InactiveStatusTooltip
            business={business}
            statusLabel={style.label}
            statusColor={style.color}
            statusBg={style.bg}
          />
        );
      }

      return (
        <Typography
          noWrap
          sx={{
            color: style.color,
            bgcolor: style.bg,
            borderRadius: '100px',
            padding: { xs: '4px 8px', sm: '4px 13px' },
            width: 'fit-content',
            fontSize: { xs: '11px', sm: '12px' },
            fontWeight: 500
          }}
        >
          {style.label}
        </Typography>
      );
    }
  },
  {
    header: 'Add-ons',
    cell: (info) => {
      const business = info.row.original;
      const addonsCount = business.business_addons?.length ?? 0;

      return (
        <Typography
          component='button'
          onClick={() => onViewAddons(business)}
          sx={{
            border: 'none',
            background: 'none',
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 500,
            cursor: 'pointer',
            p: 0
          }}
        >
          {addonsCount}
        </Typography>
      );
    }
  },
  {
    header: 'Tags',
    size: 320,
    cell: (info) => {
      const business = info.row.original;
      const tags = business.tags ?? [];

      return (
        <Box display='flex' flexDirection='row' alignItems='flex-start' minWidth={160} maxWidth={280}>
          <Box display='flex' flexWrap='wrap'>
            {tags.map((tag) => (
              <BusinessTagPill key={tag.id} tag={tag} onRemove={onRemoveTag} />
            ))}
            <Typography
              component='button'
              onClick={() => onAddTags(business)}
              sx={{
                border: 'none',
                background: 'none',
                color: theme.palette.primary.main,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              + Add
            </Typography>
          </Box>
        </Box>
      );
    }
  },
  {
    header: 'Action',
    cell: (info) => {
      const isArchived = info.row.original.account_status === 'archived';
      const isPlanStarted = info.row.original.plan_started_at;
      return (
        <Box display='flex' alignItems='center'>
          <Tooltip title={isArchived ? 'View is unavailable as business is archived' : 'View'} placement='top'>
            <IconButton
              size='small'
              disabled={isArchived}
              sx={{
                padding: { xs: '4px', sm: '8px' },
                '& svg': {
                  fontSize: { xs: '18px', sm: '24px' }
                }
              }}
              onClick={() => {
                handleToggleBusiness(info.row.original);
              }}
            >
              <MdOutlineRemoveRedEye />
            </IconButton>
          </Tooltip>
          <Tooltip title={isArchived ? 'Edit is unavailable as business is archived' : 'Edit'} placement='top'>
            <IconButton
              size='small'
              disabled={isArchived}
              sx={{
                padding: { xs: '4px', sm: '8px' },
                '& svg': {
                  fontSize: { xs: '18px', sm: '24px' }
                }
              }}
              onClick={() => handleEditBusiness(info.row.original)}
            >
              <MdOutlineEdit />
            </IconButton>
          </Tooltip>

          <ResetBusinessButton
            businessName={info.row.original.name}
            tooltipTitle={isPlanStarted ? 'Reset is unavailable as billing has started' : 'Reset Business'}
            disabled={isArchived || Boolean(isPlanStarted)}
            businessId={Number(info.row.original.id)}
            onReset={onReset}
          />
        </Box>
      );
    }
  }
];

const getPackageDetails = (businessPackage: PackageResponse) => {
  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {businessPackage?.base_price && (
        <Typography fontSize={12} lineHeight={1.5}>
          Base Price: ${businessPackage?.base_price}/Month
        </Typography>
      )}
      {businessPackage?.available_minutes && (
        <Typography fontSize={12} lineHeight={1.5}>
          Included Minutes: {businessPackage?.available_minutes}
        </Typography>
      )}
      {businessPackage?.available_warm_call_minutes != null && (
        <Typography fontSize={12} lineHeight={1.5}>
          Included Warm Call Minutes: {businessPackage?.available_warm_call_minutes}
        </Typography>
      )}
      {businessPackage?.additional_per_min_charge && (
        <Typography fontSize={12} lineHeight={1.5}>
          Additional Minutes: ${businessPackage?.additional_per_min_charge}/Minute
        </Typography>
      )}
      {businessPackage?.additional_warm_call_charge != null && (
        <Typography fontSize={12} lineHeight={1.5}>
          Additional Warm Call Minutes: ${businessPackage?.additional_warm_call_charge}/Minute
        </Typography>
      )}
      {businessPackage?.price_details.multilingual_charge_per_min?.unit_amount && (
        <Typography fontSize={12} lineHeight={1.5}>
          Multilingual Charges:: ${businessPackage?.price_details.multilingual_charge_per_min?.unit_amount}/Minute
        </Typography>
      )}
      {businessPackage?.price_details.location_limit?.limit && (
        <Typography fontSize={12} lineHeight={1.5}>
          Location Limit: {businessPackage?.price_details.location_limit?.limit}
        </Typography>
      )}
      <Typography fontSize={12} lineHeight={1.5}>
        Integration Charges: {businessPackage?.price_details.integration_charge?.enabled ? 'ON' : 'OFF'}
      </Typography>
      {businessPackage?.price_details.storage_charge_per_gb?.unit_amount && (
        <Typography fontSize={12} lineHeight={1.5}>
          Storage per GB: ${businessPackage?.price_details.storage_charge_per_gb?.unit_amount}
        </Typography>
      )}
    </Box>
  );

  if (!businessPackage) return;
  return (
    <Box
      display='flex'
      flexDirection='row'
      alignItems='center'
      gap={0.5}
      sx={{ minWidth: { xs: '120px', sm: 'auto' } }}
    >
      <Typography
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: { xs: 'nowrap', sm: 'normal' },
          maxWidth: { xs: '100px', sm: '200px', md: 'none' },
          fontSize: { xs: '14px', sm: '16px' }
        }}
      >
        {businessPackage.name}
      </Typography>
      <Tooltip
        title={tooltipContent}
        placement='top'
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: 'black',
              color: 'white',
              boxShadow: 3,
              fontSize: '0.85rem',
              borderRadius: 1,
              p: 1,
              lineHeight: 1.2
            }
          },
          arrow: {
            sx: { color: 'black' }
          }
        }}
      >
        <span>
          <MdInfoOutline style={{ cursor: 'pointer', marginTop: '4px' }} />
        </span>
      </Tooltip>
    </Box>
  );
};
